import { useState } from "react";
import { motion } from "framer-motion";
import { WORLD_OBJECTS } from "@/lib/data";
import { CONFIG } from "@/lib/config";
import { SectionHead, Reveal, SHELL, Mono } from "@/components/bits";

export default function CapitalWorld() {
    const [active, setActive] = useState(0);
    const obj = WORLD_OBJECTS[active];
    return (
        <section id="capital" className="relative py-24 md:py-32 border-t border-white/10">
            <SHELL>
                <SectionHead
                    index="01"
                    label="THE REAL ECONOMY"
                    title={"THE WORLD IS FULL OF CAPITAL."}
                    sub="Most of it is still fragmented across traditional financial infrastructure."
                />

                <Reveal>
                    <div className="relative rounded-lg overflow-hidden border border-white/10 mb-10" data-testid="capital-world-visual">
                        <img
                            src={CONFIG.ARTICLE_BANNER}
                            alt="Real-world assets: cities, farmland, cargo, energy and institutions connected across a globe"
                            className="w-full h-56 sm:h-72 md:h-[420px] object-cover"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-vera-bg via-vera-bg/20 to-transparent" aria-hidden="true" />
                        <div className="absolute bottom-4 left-4 right-4 flex flex-wrap gap-2">
                            {WORLD_OBJECTS.map((o, i) => (
                                <button
                                    key={o.id}
                                    type="button"
                                    onMouseEnter={() => setActive(i)}
                                    onFocus={() => setActive(i)}
                                    onClick={() => setActive(i)}
                                    data-testid={`world-object-${o.id}`}
                                    className={`font-mono text-[9px] tracking-[0.2em] px-3 py-2 border backdrop-blur-md transition-all ${i === active ? "border-vera-gold text-vera-gold2 bg-vera-gold/15" : "border-white/15 text-vera-white/70 bg-vera-bg/60 hover:border-vera-blue/60"}`}
                                >
                                    {o.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </Reveal>

                <div className="grid lg:grid-cols-2 gap-8 items-start">
                    <Reveal delay={0.1}>
                        <p className="text-vera-mut text-base leading-relaxed max-w-xl">
                            Buildings, warehouses, containers, farmland, energy, invoices, institutions — every object above represents real economic
                            activity. Today, most of that activity produces value without producing capital that can move. Inspect an object: it has a
                            type, an economy, a cash flow — and, for now, no onchain existence.
                        </p>
                        <p className="font-mono text-[9px] tracking-[0.2em] text-vera-mut/60 mt-6">CONCEPTUAL LABELS ONLY — NO FINANCIAL VALUES ARE PUBLISHED</p>
                    </Reveal>
                    <motion.div
                        key={obj.id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="glass-card p-6 md:p-8"
                        data-testid="world-object-detail"
                    >
                        <div className="flex items-center justify-between mb-6">
                            <Mono className="text-vera-gold2">OBJECT PROFILE</Mono>
                            <span className="font-mono text-[9px] border border-vera-gold/40 text-vera-gold2 px-2 py-0.5">CONCEPTUAL</span>
                        </div>
                        <h3 className="text-xl font-bold tracking-wide mb-6">{obj.label}</h3>
                        <dl>
                            {Object.entries(obj.fields).map(([k, v]) => (
                                <div key={k} className="flex items-center justify-between gap-4 py-3 border-b border-white/5">
                                    <dt className="font-mono text-[10px] tracking-[0.2em] text-vera-mut">{k}</dt>
                                    <dd className="font-mono text-[10px] tracking-[0.15em] text-vera-white text-right">{v}</dd>
                                </div>
                            ))}
                        </dl>
                    </motion.div>
                </div>
            </SHELL>
        </section>
    );
}
